import { autoinject } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { LookupService } from 'app/core/services/lookup-service';
import { Toaster } from 'app/shared/lib/toaster';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { IPopup } from 'app/shared/elements/popup-base';
import { deepClone } from 'app/shared/lib/utilities';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { validate } from 'app/shared/elements/ess-rule';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class CredentialsPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected credentials: ReadonlyArray<Core.Credential> | null = null;
    protected editedCredential: Core.Credential = {};

    protected canModifyCredentials: boolean = false;

    protected validate = validate;

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;
    protected includeInactive: boolean = false;

    ////****************** Element References ***************************************////
    private $credentialModal!: IPopup;
    protected $credentialForm!: IForm;
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: LookupService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected async activate(): Promise<void> {
        await this.checkPermissions();
        await this.reload();
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }

        await this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadCredentials();
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.canModifyCredentials = this.accountContext.hasPermission('Core.Credential.Update');
    }

    protected saveCredential(credential: Core.Credential | null = null): void {
        this.editedCredential = credential ? deepClone(credential) : {};
        this.$credentialModal.open();
    }

    protected closeCredentialModal(): void {
        if(this.$credentialForm.reset) {
            this.$credentialForm.reset();
        }
        this.$credentialModal.close();
    }

    protected handleCredentialFormSuccess({ detail }: FormSuccessCustomEvent): void {
        this.$credentialModal.close();

        if(detail.form && detail.form.reset) {
            detail.form.reset();
        }

        this.reload();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadCredentials(): Promise<void> {
        const includes: string[] = ['metadata.modified.byUser'];

        const options: Partial<IQueryLoadOptions> = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: this.includeInactive
        };

        try {
            this.state.beginLoad();

            const response = await this.service.loadCredentials(options, includes);
            this.credentials = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateCredential(credential: Core.Credential): Promise<void> {
        if(!credential.id) return;

        // Show confirmation dialog only if deactivating
        if(credential.isActive) {
            const result = await confirmation.showDeactivate('core:credential.$', 1, false);
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }

            if(!(result.dialogResponse === confirmation.options.OK)) {
                return;
            }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateCredential(credential.id, !credential.isActive);
            isSuccessful = response.isSuccessful;
            this.reload();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.activate('core:credential.$', !credential.isActive, isSuccessful);
            this.state.endSave();
        }
    }
}
